import {
  BusinessHomeRoute,
  type BusinessHomeView,
} from "@/design/BusinessHome";
import { PageShell } from "@/design/ui";
import { AppLink } from "~/components/AppLink";
import { BusinessPage } from "~/components/BusinessChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { useQuery } from "~/lib/useQuery";

/**
 * `/business`, the route every business member lands on (R22 T14).
 *
 * Two screens on one route: a member with a clinic gets
 * `docs/ux/screens/BusinessHomeScreen.md`, and an unaffiliated member gets
 * `RegistrationStatusScreen.md`. **The server decides which**, and returns it as
 * a discriminated union, because that choice is about who is calling rather
 * than about what a client feels like rendering.
 *
 * A failed read is `kind: "error"` in the payload for the same reason: this
 * route is somebody's home, and it must render *something* rather than a blank
 * dashboard. When the request itself fails the client supplies that branch
 * locally, which is the one case the server cannot answer.
 */
interface HomePayload {
  view: BusinessHomeView;
  unread: number;
}

export function BusinessHomeScreen() {
  const home = useQuery<HomePayload>("/business/home");

  return (
    <BusinessPage>
      <PageShell>
        {home.loading && !home.data ? (
          <ScreenPending />
        ) : (
          <BusinessHomeRoute
            view={home.data?.view ?? { kind: "error" }}
            unread={home.data?.unread ?? 0}
            configured={home.failure !== "not_configured"}
            Link={AppLink}
          />
        )}
      </PageShell>
    </BusinessPage>
  );
}
